import { ReactNode } from "react";
import { useReportsApproved } from "../../../hooks/useDashboardQueries";
import {
	DashboardReportType,
	ReportsApprovedResponse,
} from "../../../types/dashboard.types";
import { cardStatus } from "../cardStatus";
import { BADGES, reportTypeOptions, yearOptions } from "../dashboardConfig";
import ExpandedWidgetModal from "../ExpandedWidgetModal";
import {
	MultiSelectField,
	ProjectField,
	SelectField,
	TenantField,
} from "../FilterControls";
import { TenantChoice } from "../tenantChoice";
import { useDraftFilters, useHasOpened } from "../widgetHooks";
import WidgetState from "../WidgetState";
import { approvedEmptyText, hasNoApproved } from "./reportsApproved";

interface ReportsApprovedExpandedProps {
	open: boolean;
	onClose: () => void;
	title: string;
	choice: TenantChoice;
	initialTenant: string;
	initialYear: number;
	/** The widget's chart for the applied filters. */
	renderChart: (
		data: ReportsApprovedResponse,
		onlyTypes: DashboardReportType[],
		context: string
	) => ReactNode;
}

/** The expanded view both Reports approved widgets share. */
function ReportsApprovedExpanded({
	open,
	onClose,
	title,
	choice,
	initialTenant,
	initialYear,
	renderChart,
}: ReportsApprovedExpandedProps) {
	const hasOpened = useHasOpened(open);
	const filters = useDraftFilters<{
		tenant: string;
		year: number;
		types: DashboardReportType[];
		project?: string;
	}>({ tenant: initialTenant, year: initialYear, types: [] });
	const { applied, draft } = filters;
	const { target, label } = choice.resolve(applied.tenant);
	const draftTarget = choice.resolve(draft.tenant).target;
	const query = useReportsApproved(hasOpened ? target : null, applied.year, {
		project: target?.scope === "tenant" ? applied.project : undefined,
	});
	const context = `${label} · ${applied.year}`;

	return (
		<ExpandedWidgetModal
			open={open}
			onClose={onClose}
			badge={BADGES.reports}
			title={title}
			context={context}
			onApply={filters.apply}
			onReset={filters.reset}
			filters={
				<>
					<TenantField
						value={draft.tenant}
						options={choice.options}
						onChange={(tenant) =>
							filters.update({ tenant, project: undefined })
						}
					/>
					<SelectField
						label="Year"
						value={draft.year}
						options={yearOptions()}
						onChange={(year) => filters.update({ year })}
					/>
					<MultiSelectField
						label="Report type"
						options={reportTypeOptions(query.data?.types)}
						values={draft.types}
						onChange={(types) =>
							filters.update({ types: types as DashboardReportType[] })
						}
					/>
					{draftTarget?.scope === "tenant" && (
						<ProjectField
							tenantKey={draftTarget.tenantKey}
							value={draft.project}
							onChange={(project) => filters.update({ project })}
							help="Audits aren't linked to projects"
						/>
					)}
				</>
			}
		>
			<WidgetState
				status={cardStatus(query, (data) => hasNoApproved(data, applied.types))}
				emptyText={approvedEmptyText(applied.year)}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && renderChart(query.data, applied.types, context)}
			</WidgetState>
		</ExpandedWidgetModal>
	);
}

export default ReportsApprovedExpanded;
